import { AlertCircle, AlertTriangle, CheckCircle2, Info } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

type Tone = 'info' | 'success' | 'warning' | 'error';

const TONES: Record<Tone, { className: string; Icon: typeof Info; role: 'status' | 'alert' }> = {
  info: { className: 'bg-brand-soft text-brand-ink', Icon: Info, role: 'status' },
  success: { className: 'bg-positive-soft text-positive', Icon: CheckCircle2, role: 'status' },
  // "It worked, but not entirely" — announced politely, because the operation
  // did complete. An import with a handful of bad rows is the shape of this.
  warning: { className: 'bg-warning-soft text-warning', Icon: AlertTriangle, role: 'status' },
  // Errors are announced immediately rather than politely queued.
  error: { className: 'bg-danger-soft text-danger', Icon: AlertCircle, role: 'alert' },
};

export function Alert({
  tone = 'info',
  title,
  children,
  className,
}: {
  tone?: Tone;
  title?: string;
  children?: ReactNode;
  className?: string;
}) {
  const { className: toneClass, Icon, role } = TONES[tone];
  return (
    <div
      role={role}
      className={cn('flex gap-3 rounded-md px-4 py-3 text-sm', toneClass, className)}
    >
      <Icon className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
      <div className="space-y-0.5">
        {title ? <p className="font-medium">{title}</p> : null}
        {children ? <div className="opacity-90">{children}</div> : null}
      </div>
    </div>
  );
}
